import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  useMediaQuery,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Icon } from "@iconify/react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import {
  teamExcelDownloadUrl,
  rfidTagTransactionsUrl,
  rfidTagTransactionsKpiUrl,
} from "src/react-query/endPoints";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Token from "src/util/Token";
import { alpha, useTheme } from "@mui/material/styles";
import { BaseTable } from "src/components/BaseTable/BaseTable";

const RFIDTagTransactionDetails = ({ open, onClose, rfid }) => {
  const { teamId } = useSelectedTeam();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [filters, setFilters] = useState({
    type: "all",
    from: null,
    to: null,
  });
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [kpis, setKpis] = useState({
    credits: 0,
    debits: 0,
    balance: 0,
    totalTransactions: 0,
  });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20,
    total: 0,
  });
  const fieldSurface = theme.palette.mode === "dark"
    ? alpha(theme.palette.primary.main, 0.25)
    : alpha(theme.palette.primary.main, 0.08);
  const focusRing = `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}`;
  const formFieldStyles = {
    minWidth: 150,
    flex: 1,
    maxWidth: 180,
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5,
      backgroundColor: fieldSurface,
      transition: "box-shadow 0.2s ease, border-color 0.2s ease",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(theme.palette.primary.main, 0.4),
      },
      "&.Mui-focused": {
        boxShadow: focusRing,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiSelect-icon": {
      color: theme.palette.primary.main,
    },
  };
  const labelStyles = {
    fontWeight: 600,
    color: "text.primary",
    px: 0.5,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 1,
    lineHeight: 1.4,
  };
  const datePickerSlotProps = {
    textField: {
      size: "small",
      fullWidth: true,
      sx: {
        "& .MuiOutlinedInput-root": {
          borderRadius: 2.5,
          backgroundColor: fieldSurface,
          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
          "& fieldset": {
            borderColor: alpha(theme.palette.primary.main, 0.25),
          },
          "&:hover fieldset": {
            borderColor: alpha(theme.palette.primary.main, 0.4),
          },
          "&.Mui-focused": {
            boxShadow: focusRing,
          },
          "&.Mui-focused fieldset": {
            borderColor: theme.palette.primary.main,
          },
        },
      },
    },
  };
  const summaryBalanceRef = useRef(0);
  const previousFilterKeyRef = useRef();
  const previousRfidRef = useRef();
  const previousOpenRef = useRef();
  const previousTeamRef = useRef();

  const filterQuery = useMemo(() => {
    const params = {};
    if (filters.type !== "all") params.type = filters.type;
    if (filters.from)
      params.from = dayjs(filters.from).format("YYYY-MM-DD");
    if (filters.to) params.to = dayjs(filters.to).format("YYYY-MM-DD");
    return params;
  }, [filters]);

  const filterKey = useMemo(() => JSON.stringify(filterQuery), [filterQuery]);

  const paginationQuery = useMemo(
    () => ({
      offset: pagination.page * pagination.pageSize,
      limit: pagination.pageSize,
    }),
    [pagination.page, pagination.pageSize]
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  useEffect(() => {
    if (!rfid || !open) {
      if (!open) {
        previousOpenRef.current = open;
      }
      return;
    }

    const controller = new AbortController();
    let isMounted = true;
    const filtersChanged = filterKey !== previousFilterKeyRef.current;
    const rfidChanged = rfid !== previousRfidRef.current;
    const teamChanged = teamId !== previousTeamRef.current;
    const openedNow = !previousOpenRef.current && open;

    const shouldFetchSummary =
      filtersChanged || rfidChanged || teamChanged || openedNow;

    previousFilterKeyRef.current = filterKey;
    previousRfidRef.current = rfid;
    previousTeamRef.current = teamId;
    previousOpenRef.current = open;

    const headers = {
      Authorization: `Bearer ${Token.get()}`,
      "Content-Type": "application/json",
    };

    const fetchSummary = async () => {
      const summaryUrl = rfidTagTransactionsKpiUrl(
        teamId,
        rfid,
        filterQuery
      );
      const res = await fetch(summaryUrl, {
        headers,
        signal: controller.signal,
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || "Failed to fetch RFID KPIs");
      return text ? JSON.parse(text) : {};
    };

    const fetchTransactions = async (openingBalance = 0) => {
      const apiUrl = rfidTagTransactionsUrl(
        teamId,
        rfid,
        { ...paginationQuery, ...filterQuery }
      );
      const res = await fetch(apiUrl, {
        headers,
        signal: controller.signal,
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text || "Failed to fetch RFID transactions");
      const data = text ? JSON.parse(text) : {};

      if (!isMounted) return;

      setPagination((prev) => ({
        ...prev,
        total: data.total || 0,
      }));

      const sortedTxns = [...(data.data || [])].sort(
        (a, b) => new Date(b.starttime) - new Date(a.starttime)
      );

      let runningBalance = Number(openingBalance ?? 0);

      const mapped = sortedTxns.map((txn) => {
        const isCredit = (txn.payment_type || "").toLowerCase() === "credit";
        const amountValue = Number(txn.totalAmountCharged || 0);
        const sign = isCredit ? "+" : "-";
        const displayAmount = `${sign}₹${amountValue.toFixed(2)}`;
        const amountColor = isCredit ? "green" : "red";

        const displayBalance = `₹${runningBalance.toFixed(2)}`;
        runningBalance = isCredit
          ? runningBalance - amountValue
          : runningBalance + amountValue;

        let detail = txn.chargepoint_name || "";
        if (txn.stop_reason) detail += ` / ${txn.stop_reason}`;
        const duration = txn.minutes ? `${txn.minutes}min` : "";
        const energy = txn.totalEnergy ? `${txn.totalEnergy}kWh` : "";
        const extras = [duration, energy].filter(Boolean).join(" • ");
        if (extras) detail += (detail ? " — " : "") + extras;

        return {
          id: `${txn.transactionId || "noid"}-${txn.starttime}`,
          date: txn.starttime,
          type: txn.payment_type,
          description: detail,
          amount: displayAmount,
          amountColor,
          balance: displayBalance,
          reference: txn.transactionId,
        };
      });

      setRows(mapped);
    };

    const loadData = async () => {
      setLoading(true);
      let openingBalance = summaryBalanceRef.current || 0;

      if (shouldFetchSummary) {
        try {
          const summaryData = await fetchSummary();
          if (isMounted) {
            const totals = {
              credits: summaryData.kpis?.totalCredits || 0,
              debits: summaryData.kpis?.totalDebits || 0,
              balance: summaryData.kpis?.currentBalance || 0,
              totalTransactions: summaryData.kpis?.totalTransactions || 0,
            };
            summaryBalanceRef.current = totals.balance;
            openingBalance = totals.balance;
            setKpis(totals);
          }
        } catch (error) {
          console.error("Summary fetch error:", error);
        }
      }

      try {
        await fetchTransactions(openingBalance);
      } catch (error) {
        console.error("Transactions fetch error:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [
    rfid,
    filterKey,
    filterQuery,
    open,
    teamId,
    paginationQuery,
    pagination.page,
    pagination.pageSize,
  ]);

  const handleClearFilters = () => {
    setFilters({ type: "all", from: null, to: null });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleExcelDownload = async () => {
    if (!rfid || !teamId) return;
    setExcelLoading(true);
    const token = Token.get();

    try {
      const fetchUrl = rfidTagTransactionsUrl(teamId, rfid, {
        offset: 0,
        limit: 999999,
        ...(filters.type !== "all" && { type: filters.type }),
        ...(filters.from && {
          from: dayjs(filters.from).format("YYYY-MM-DD"),
        }),
        ...(filters.to && { to: dayjs(filters.to).format("YYYY-MM-DD") }),
      });
      
      const fetchResponse = await fetch(fetchUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!fetchResponse.ok) {
        throw new Error(`HTTP error! status: ${fetchResponse.status}`);
      }

      const fetchData = await fetchResponse.json();
      
      if (!fetchData.data?.length) {
        setSuccessMessage("No transactions found to download");
        return;
      }

      const excelData = fetchData.data.map(txn => ({
        "Date & Time": txn.starttime ? dayjs(txn.starttime).format("YYYY-MM-DD HH:mm") : "",
        "Type": txn.payment_type,
        "Description": (() => {
          let detail = txn.chargepoint_name || "";
          if (txn.stop_reason) detail += ` / ${txn.stop_reason}`;
          const duration = txn.minutes ? `${txn.minutes}min` : "";
          const energy = txn.totalEnergy ? `${txn.totalEnergy}kWh` : "";
          const extras = [duration, energy].filter(Boolean).join(" • ");
          if (extras) detail += (detail ? " — " : "") + extras;
          return detail;
        })(),
        "Amount (₹)": Number(txn.totalAmountCharged || 0).toFixed(2),
        "Reference": txn.transactionId
      }));

      const response = await fetch(teamExcelDownloadUrl(teamId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: excelData,
          filename: `rfid-transactions-${rfid}`
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]
        : `rfid-transactions-${rfid}.xlsx`;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccessMessage(`Excel report downloaded successfully! Total records: ${excelData.length}`);
    } catch (error) {
      setSuccessMessage(`Failed to download Excel report: ${error.message}`);
    } finally {
      setExcelLoading(false);
    }
  };

  const columns = [
    {
      field: "date",
      headerName: "Date & Time",
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) =>
        params.value ? dayjs(params.value).format("YYYY-MM-DD HH:mm") : "",
    },
    { field: "type", headerName: "Type", flex: 0.8, minWidth: 100 },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <span style={{ color: params.row.amountColor, fontWeight: 600 }}>
          {params.value}
        </span>
      ),
    },
    { field: "balance", headerName: "Balance", flex: 0.8, minWidth: 100 },
    { field: "reference", headerName: "Reference", flex: 1, minWidth: 130 },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSuccessMessage("")} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle>RFID Tag: {rfid} - Transaction Details</DialogTitle>
        <DialogContent>
          {/* Filters */}
          <Stack
            direction="row"
            spacing={2}
            mb={2}
            sx={{
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <FormControl size="small" sx={formFieldStyles}>
              <InputLabel shrink sx={labelStyles}>
                Type
              </InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
                <MenuItem value="debit">Debit</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ flex: 1, minWidth: 160, maxWidth: 180 }}>
              <DatePicker
                label="From"
                value={filters.from}
                onChange={(date) => handleFilterChange("from", date)}
                slotProps={datePickerSlotProps}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 160, maxWidth: 180 }}>
              <DatePicker
                label="To"
                value={filters.to}
                onChange={(date) => handleFilterChange("to", date)}
                slotProps={datePickerSlotProps}
              />
            </Box>

            <Button
              onClick={handleClearFilters}
              variant="outlined"
              size="small"
              sx={{ whiteSpace: "nowrap", height: "40px" }}
            >
              Clear Filters
            </Button>
          </Stack>

          {/* KPIs */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            mb={2}
            sx={{ flexWrap: "wrap" }}
          >
            <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
              <Typography variant="subtitle2">Total Credits</Typography>
              <Typography variant="h6" color="success.main">
                ₹{Number(kpis.credits).toFixed(2)}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
              <Typography variant="subtitle2">Total Debits</Typography>
              <Typography variant="h6" color="error.main">
                ₹{Number(kpis.debits).toFixed(2)}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
              <Typography variant="subtitle2">Current Balance</Typography>
              <Typography variant="h6">
                ₹{Number(kpis.balance).toFixed(2)}
              </Typography>
            </Paper>
          </Stack>

          <BaseTable
            rows={rows}
            columns={columns}
            page={pagination.page}
            pageSize={pagination.pageSize}
            rowCount={pagination.total}
            onPageChange={(newPage) =>
              setPagination((prev) => ({
                ...prev,
                page: newPage,
              }))
            }
            onPageSizeChange={(newPageSize) =>
              setPagination((prev) => ({
                ...prev,
                pageSize: newPageSize,
                page: 0,
              }))
            }
            serverRowFetcher
            loading={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            variant="contained"
            color="success"
            startIcon={
              excelLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon icon="eva:download-fill" width={20} height={20} />
              )
            }
            onClick={handleExcelDownload}
            disabled={excelLoading || !rfid}
          >
            {excelLoading ? "Downloading..." : "Download Excel"}
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default RFIDTagTransactionDetails;
